import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { ArchiveRestore, Archive } from "lucide-react";
import { CustomLoading } from "components/CustomLoading";
import Breadcrumb, { BreadcrumbPill } from "./Breadcrumb";
import { RowIcon, formatRelative } from "./category-rows";

interface ArchivedCategory {
    id: string;
    name: string;
    icon: string | null;
    color: string | null;
    archivedAt: string;
}

interface ArchivedPage {
    id: string;
    title: string;
    categoryId: string;
    ownerId: string | null;
    archivedAt: string;
}

interface Props {
    // active categories, used to label which category an archived page belongs to
    categories: { id: string; name: string }[];
    onRestored: () => void;
}

export default function ArchivedView({ categories, onRestored }: Props) {
    const [cats, setCats] = useState<ArchivedCategory[]>([]);
    const [pages, setPages] = useState<ArchivedPage[]>([]);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        setLoading(true);
        axios.get("/api/manuals/archive")
            .then(res => {
                if (res.data.success) {
                    setCats(res.data.categories || []);
                    setPages(res.data.pages || []);
                } else {
                    toast.error(res.data.message || "Failed to load archive");
                }
            })
            .catch(err => toast.error(err.response?.data?.message || "Failed to load archive"))
            .finally(() => setLoading(false));
    }, [reloadKey]);

    function restore(kind: "categories" | "pages", id: string, label: string) {
        axios.post(`/api/manuals/${kind}/${id}/restore`)
            .then(res => {
                if (res.data.success) {
                    toast.success(`Restored "${label}"`);
                    setReloadKey(k => k + 1);
                    onRestored();
                } else {
                    toast.error(res.data.message || "Failed to restore");
                }
            })
            .catch(err => toast.error(err.response?.data?.message || "Failed to restore"));
    }

    const pills: BreadcrumbPill[] = [{ kind: "category", label: "Archive", iconName: "archive", iconColor: null }];
    const catNameById = new Map<string, string>(categories.map(c => [c.id, c.name]));
    cats.forEach(c => catNameById.set(c.id, c.name));

    return (
        <div className="m-cat-page">
            <Breadcrumb pills={pills} />

            {loading ? (
                <div className="m-loading"><CustomLoading /></div>
            ) : cats.length === 0 && pages.length === 0 ? (
                <div className="m-archive-empty">
                    <Archive size={28} />
                    <p>Nothing is archived.</p>
                </div>
            ) : (
                <>
                    {cats.length > 0 && (
                        <>
                            <div className="m-archive-section">Categories</div>
                            {cats.map(c => (
                                <div key={c.id} className="m-archive-row">
                                    <RowIcon kind="folder" iconName={c.icon} iconColor={c.color} />
                                    <span className="m-archive-row-title">{c.name}</span>
                                    <span className="m-archive-row-meta">archived {formatRelative(c.archivedAt)}</span>
                                    <button className="m-btn m-btn-ghost" onClick={() => restore("categories", c.id, c.name)}>
                                        <ArchiveRestore size={14} /> Restore
                                    </button>
                                </div>
                            ))}
                        </>
                    )}

                    {pages.length > 0 && (
                        <>
                            <div className="m-archive-section">Pages</div>
                            {pages.map(p => (
                                <div key={p.id} className="m-archive-row">
                                    <RowIcon kind="page" />
                                    <span className="m-archive-row-title">{p.title}</span>
                                    <span className="m-archive-row-meta">
                                        {catNameById.get(p.categoryId) || "Unknown category"} · archived {formatRelative(p.archivedAt)}
                                    </span>
                                    <button className="m-btn m-btn-ghost" onClick={() => restore("pages", p.id, p.title)}>
                                        <ArchiveRestore size={14} /> Restore
                                    </button>
                                </div>
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
